import { CreatableSelectOption } from 'ud-form/components/select/component';
import { Formik, FormikHelpers } from 'formik';
import React, { useCallback } from 'react';
import { announceNewTeam } from 'modules/teams/store/championship-teams/actions';

import { AppDispatch } from 'store/store';
import { Team } from '../../../domain/interfaces/team';
import TeamLogo from 'ud-ui/components/team-logo';
import UDButton from 'ud-ui/components/button';
import UDText from 'ud-ui/components/text';
import styled from '@emotion/styled';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import AsyncSelect from 'react-select/async';
import { LoadingMessage, SelectComponent } from 'modules/teams/ui/hooks/use-modal-add-player/styles';
import teamsRepository from 'modules/teams/domain/repositories/teams.repository';
import { SportType } from 'modules/core/domain/enums/sport-type';
import { toast } from 'react-toastify';

const TeamSelectOption = styled.div`
  display: flex;
  align-items: center;

  .team-select-logo {
    margin-right: 8px;
  }
`;

function TeamOption(props: { team: Team }) {
  return (
    <TeamSelectOption>
      <TeamLogo className="team-select-logo" url={props.team.logo?.url} size={32} />
      {props.team.name}
    </TeamSelectOption>
  );
}

export const teamToTeamOption = (team: Team): CreatableSelectOption<number, Team> => {
  return {
    label: <TeamOption team={team} />,
    value: team.id,
    data: team,
  };
};

const AddTeamApplicationFormWrapper = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
`;

const AddTeamApplicationFormContainer = styled.div`
  padding: 16px 20px 24px 20px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow:
    0 0 1px rgba(26, 26, 41, 0.08),
    0 8px 16px rgba(26, 26, 41, 0.08);
  margin-bottom: 42px;
`;

const AddTeamApplicationFormFields = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;

  > div {
    margin: 0 5px;
    &:first-child {
      margin-left: 0;
    }
    &:last-child {
      margin-right: 0;
    }
  }
`;

export const TeamSelectComponent = {
  ...SelectComponent,
  SingleValue: (props: any) => {
    const value = props.data?.data;
    return <TeamOption team={value} />;
  },
};

type AddTeamApplicationFormProps = {
  championshipId: number;
  existTeamsId: number[];
};

const AddTeamApplicationForm = ({ championshipId }: AddTeamApplicationFormProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { info } = useOrganizer();
  const navigate = useNavigate();

  type FormValues = {
    team?: CreatableSelectOption<any, Team>;
  };

  const handleSubmit = useCallback(
    async (values: FormValues, { resetForm }: FormikHelpers<FormValues>) => {
      const { team } = values;
      if (!team) {
        return;
      }

      if (team.__isNew__) {
        if (!info) {
          return;
        }

        toast.warn('Функционал упразднён. Если вы увидели эту ошибку, пожалуйста, сообщите в поддержку.');
        throw Error('Functionality is disabled. If you see this error, please contact support.');

        // dispatch(
        //   createChampionshipTeam({
        //     tournamentId: info.id,
        //     name: String(team.value),
        //   }),
        // )
        //   .then((result) => {
        //     const createdTeam = unwrapResult(result);
        //
        //     dispatch(
        //       announceNewTeam({
        //         team: {
        //           id: createdTeam.id,
        //           name: createdTeam.name,
        //           logo: createdTeam.logo,
        //           createdAt: createdTeam.createdAt,
        //           players: [],
        //         },
        //         championshipId,
        //       }),
        //     );
        //
        //     return createdTeam;
        //   })
        //   .then((createdTeam) => {
        //     resetForm();
        //     navigate(`${createdTeam.id}`);
        //   });
      } else {
        const detailTeam = await teamsRepository.getTournamentTeam({ teamId: team.data!.id });

        await dispatch(
          announceNewTeam({
            team: {
              id: detailTeam.id,
              name: detailTeam.name,
              logo: detailTeam.logo,
              players: detailTeam.players.filter((player) => !player.isReal),
            },
            championshipId,
          }),
        ).unwrap();

        resetForm();
        navigate(`${team.data!.id}`);
      }
    },
    [championshipId, dispatch, info, navigate],
  );

  const handleLoadOptions = useCallback(
    async (inputValue: string) => {
      if (!info?.id) return [];
      const teams = await teamsRepository.getTournamentTeams({
        tournamentId: info.id,
        limit: 10,
        offset: 0,
        sportType: info?.sportType ?? SportType.football,
        namePart: inputValue,
      });

      return teams.map(teamToTeamOption);
    },
    [info],
  );

  return (
    <AddTeamApplicationFormWrapper>
      <Formik<FormValues> initialValues={{ team: undefined }} onSubmit={handleSubmit}>
        {(formProps) => {
          return (
            <form onSubmit={formProps.handleSubmit}>
              <AddTeamApplicationFormContainer>
                <UDText type="subhead" className="bold">
                  Добавить новую команду
                </UDText>

                <AddTeamApplicationFormFields>
                  <AsyncSelect
                    key="add-new-team"
                    className="w-90"
                    name="team"
                    placeholder="Введите название"
                    loadingMessage={LoadingMessage}
                    components={TeamSelectComponent}
                    cacheOptions
                    loadOptions={handleLoadOptions}
                    value={formProps.values.team}
                    onChange={(option) => formProps.setFieldValue('team', option)}
                    onBlur={() => formProps.setFieldTouched('team', true)}
                  />
                  <div>
                    <UDButton
                      type="submit"
                      variant="primary"
                      loading={formProps.isSubmitting}
                      disabled={!formProps.values.team || !formProps.dirty}
                    >
                      Добавить
                    </UDButton>
                  </div>
                </AddTeamApplicationFormFields>
              </AddTeamApplicationFormContainer>
            </form>
          );
        }}
      </Formik>
    </AddTeamApplicationFormWrapper>
  );
};

export default AddTeamApplicationForm;
